/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link, useNavigate } from 'react-router';
import { encodeFilters } from '@/shared/hooks/use-url-filters';
import { Text, UnstyledButton } from '@/shared/ui';
import { CategoryDonut, type Slice } from './Overview.categories.donut';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * How many categories are drawn under their own name.
 *
 * Six is the accent ramp's length, and a seventh mark would have to reuse
 * a colour already spent -- two slices painted the same is not a chart, it
 * is a chart with a lie in it. It is also about as many slices as a bar
 * can hold before one is thinner than its own border. The tail is summed
 * into a single "outras" slice rather than dropped, so the marks still add
 * up to the total in the middle.
 */
const SHOWN = 6;

/**
 * The slice for listings the histogram cannot place.
 *
 * `category_counts` only counts categories in the backend's allowed
 * vocabulary, so a listing whose category is null, empty or off-vocabulary
 * is in the book and in no bucket. Drawn in a muted neutral rather than an
 * accent: it is not a category, it is the absence of one, and it reads as
 * the to-do it is.
 */
const UNCATEGORISED_COLOUR = 'var(--border-medium)';

/**
 * The colour of the summed tail.
 *
 * A muted neutral for the same reason as the one above, one step darker so
 * the two remainders are not the same mark: "six other categories" is a
 * real part of the book, and "no category recorded" is a gap in it.
 */
const TAIL_COLOUR = 'var(--text-tertiary-soft)';

/**
 * The six accents the ramp spends, ordered so neighbours never look alike.
 *
 * Not the palette's declaration order, which put marinho (#1E3A5F) beside
 * petroleo (#2D5A6B): ΔE 20 between the two biggest slices, which is why
 * first and second place read as one wedge. The ten accents were measured
 * pairwise and this is the six-colour subset and cyclic order with the
 * widest worst adjacent pair -- ΔE 46 between any two slices that touch,
 * the wrap from the last back to the first included. Petroleo and oliva
 * are dropped; they are the two that cannot sit near marinho or salvia
 * without collapsing into them.
 *
 * ΔE 23 is still the floor across *non*-touching pairs -- the accent
 * palette is deliberately dark and low-chroma, so no ordering of it gets
 * a chart-grade spread. That floor is why the legend names every slice
 * and why the ring keeps a gap between wedges: colour is the shorthand
 * here, never the only way a slice is identified.
 */
const ACCENTS = [
  'var(--accent-marinho)', 'var(--accent-ambar)', 'var(--accent-ameixa)',
  'var(--accent-mostarda)', 'var(--accent-salvia)', 'var(--accent-cobre)',
];

/**
 * The book split by category: a ring and its legend.
 *
 * The tile above answers "how many"; this answers "of what", which is the
 * one question on this screen a number cannot answer on its own — twelve
 * houses and thirty-eight flats is a different agency from the reverse, and
 * the context line only had room to name three.
 *
 * It sits under the attention panel rather than among the tiles: a chart is
 * not a click target with a number on it, and putting it in the grid would
 * have broken the one shape the redesign bought.
 *
 * A ring on every screen, not a stacked bar on the shorter ones. The bar
 * read as a progress meter rather than a split, and the same card changing
 * mark between two layouts asked the reader to re-learn it. The ring is
 * capped so the short card is not pushed taller by it.
 *
 * @param props.t - Translation function
 * @param props.counts - Properties per category across the whole portfolio
 * @param props.total - How many properties the agency has, from the payload
 * @param props.agencyId - Active agency, for the link to the list
 */
export function CategoryBreakdown({
  t, counts, total, agencyId,
}: {
  t: TFn;
  counts: Record<string, number> | undefined;
  total: number | null | undefined;
  agencyId: string;
}) {
  const navigate = useNavigate();
  const properties = `/app/agencies/${agencyId}/properties`;

  /**
   * Where a slice points: the list, filtered to what the slice counts.
   *
   * The list keeps its filters in one base64url `f` param, so a deep link
   * has to write that param rather than a readable `?category=`; the
   * encoder is imported from the hook that reads it so the two cannot
   * drift apart.
   */
  const destination = (filter?: string[]) => (
    filter?.length
      ? `${properties}?f=${encodeFilters({ category: filter })}`
      : properties
  );

  const entries = Object.entries(counts ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  if (!entries.length) return null;

  const counted = entries.reduce((sum, [, count]) => sum + count, 0);
  // The payload's total when it has one; the histogram's sum otherwise, so a
  // payload that predates the key still renders the card it always did.
  const portfolio = typeof total === 'number' ? total : counted;
  // Clamped: a statistics document written before the last few listings
  // would otherwise draw a negative slice.
  const uncategorised = Math.max(0, portfolio - counted);

  // Everything past the sixth category, as one number rather than a tail of
  // slices in colours the ramp does not have.
  const tail = entries.slice(SHOWN).reduce((sum, [, count]) => sum + count, 0);

  /*
   * One ordered list for the mark and the legend both, so a slice cannot
   * be one colour in the ring and another in the words beside it. The two
   * remainders come last and in this order: "outras" is still categories,
   * and "sem categoria" is the absence of one.
   */
  const slices: Slice[] = [
    ...entries.slice(0, SHOWN).map(([category, count], index) => ({
      key: category,
      label: category,
      count,
      colour: ACCENTS[index],
      filter: [category],
    })),
    ...(tail > 0 ? [{
      key: '__others',
      label: t('dashboard.categories.others'),
      count: tail,
      colour: TAIL_COLOUR,
      // The tail is a real set of categories, not a leftover: naming all
      // of them turns "+2 outras" into the list of exactly those two.
      filter: entries.slice(SHOWN).map(([category]) => category),
    }] : []),
    ...(uncategorised > 0 ? [{
      key: '__uncategorised',
      label: t('dashboard.categories.uncategorised'),
      count: uncategorised,
      colour: UNCATEGORISED_COLOUR,
      // No filter: these listings are the ones with no category to filter
      // on, and `category=` with nothing in it is the unfiltered list. The
      // slice still points at the book, just without a promise it cannot
      // keep.
    }] : []),
  ];

  return (
    <Link
      to={properties}
      data-testid="overview-categories"
      className="flex min-w-0 flex-1 flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-[var(--surface-raised)] px-(--space-4) py-(--space-3) no-underline transition-shadow duration-150 hover:shadow-[var(--shadow-md)]"
    >
      <Text as="span" className="flex items-baseline gap-(--space-2)">
        <Text
          as="span"
          className="text-(length:--text-app-section) font-[var(--font-bold)] text-[var(--text-primary)]"
        >
          {t('dashboard.categories.title')}
        </Text>
        <Text as="span" className="text-(length:--text-meta) text-[var(--text-tertiary-soft)] tabular-nums">
          {t('dashboard.categories.total', { count: portfolio })}
        </Text>
      </Text>

      <CategoryDonut
        slices={slices}
        total={portfolio}
        totalLabel={t('dashboard.categories.unit')}
        onSelect={(slice) => navigate(destination(slice.filter))}
      />

      {/*
        * The legend is not optional and never abbreviates to colour alone:
        * the accent ramp's closest neighbours separate by less than a
        * reader with full colour vision can rely on, so the words are how
        * a slice is actually identified. The swatch is the cross-reference.
        */}
      <Text as="span" className="flex flex-wrap gap-x-(--space-3) gap-y-(--space-1)">
        {slices.map((slice) => (
          /*
            * The legend entry goes where its wedge goes. A reader who has
            * found the category they want has found it here, in the words
            * -- asking them to go back and hit a few pixels of arc to act
            * on it is asking them to aim at the worse target.
            *
            * A button and not a nested link: this card is already one big
            * `<a>`, and an anchor inside an anchor is invalid markup that
            * browsers resolve by closing the outer one early, which breaks
            * the rest of the card's click target. `preventDefault` stops
            * the wrapper from also navigating to the unfiltered list.
            */
          <UnstyledButton
            type="button"
            key={slice.key}
            data-testid={slice.key === '__uncategorised'
              ? 'overview-categories-uncategorised' : undefined}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              navigate(destination(slice.filter));
            }}
            className="inline-flex cursor-pointer items-center gap-(--space-1) rounded-(--radius-sm) text-(length:--text-tag) text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
          >
            <Text
              as="span"
              className="inline-block h-[8px] w-[8px] rounded-(--radius-sm)"
              style={{ background: slice.colour }}
            />
            {`${slice.label} ${slice.count}`}
          </UnstyledButton>
        ))}
      </Text>
    </Link>
  );
}
